import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  DollarSign,
  Users,
  TrendingUp,
  Link as LinkIcon,
  Copy,
  CheckCircle,
  Clock,
  ArrowUpRight,
  QrCode,
  Download,
  RefreshCw
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL || '/_/backend'}/api`;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-NZ', { 
    style: 'currency', 
    currency: 'NZD', 
    minimumFractionDigits: 2 
  }).format(amount);
};

const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('en-NZ', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const loadDealerDashboardData = async ({
  navigate,
  setCommissions,
  setDealerCode,
  setLoading,
  setQrCodeUrl,
  setReferralLink,
  setStats,
}) => {
  setLoading(true);
  try {
    const statsRes = await fetch(`${API}/dealers/stats`, { credentials: 'include' });
    if (statsRes.status === 403) {
      toast.error('Dealer access required');
      navigate('/');
      return;
    }
    if (statsRes.ok) {
      const statsData = await statsRes.json();
      setStats(statsData);
    }

    const commissionsRes = await fetch(`${API}/dealers/commissions`, { credentials: 'include' });
    if (commissionsRes.ok) {
      const commissionsData = await commissionsRes.json();
      setCommissions(commissionsData.commissions || []);
    }

    const linkRes = await fetch(`${API}/dealers/referral-link`, { credentials: 'include' });
    if (linkRes.ok) {
      const linkData = await linkRes.json();
      setReferralLink(linkData.referral_link);
      setDealerCode(linkData.dealer_code);
      setQrCodeUrl(linkData.qr_code_url);
    }
  } catch (e) {
    console.error('Error fetching dealer data:', e);
    toast.error('Failed to load dashboard');
  } finally {
    setLoading(false);
  }
};

const DealerDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [referralLink, setReferralLink] = useState('');
  const [dealerCode, setDealerCode] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadDealerDashboardData({
      navigate,
      setCommissions,
      setDealerCode,
      setLoading,
      setQrCodeUrl,
      setReferralLink,
      setStats,
    });
  }, [navigate]);

  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-blue-100 text-blue-800',
    paid: 'bg-green-100 text-green-800'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="pt-32 flex items-center justify-center">
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <main className="pt-28 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div>
              <h1 className="font-outfit text-3xl font-bold text-black mb-2">
                Dealer Dashboard
              </h1>
              <p className="text-gray-600">
                Track your referrals and commissions
              </p>
            </div>
            <div className="flex items-center gap-3 mt-4 md:mt-0">
              <Badge variant="outline" className="px-3 py-1 text-sm">
                Code: {dealerCode}
              </Badge>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => loadDealerDashboardData({
                  navigate,
                  setCommissions,
                  setDealerCode,
                  setLoading,
                  setQrCodeUrl,
                  setReferralLink,
                  setStats,
                })}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Total Referrals
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">{stats?.total_referrals || 0}</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {stats?.this_month_referrals || 0} this month
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Total Earned
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-[#00D47E]">
                    {formatCurrency(stats?.total_commission_earned || 0)}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {formatCurrency(stats?.this_month_commission || 0)} this month
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Pending
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-yellow-600">
                    {formatCurrency(stats?.total_commission_pending || 0)}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {stats?.pending_referrals || 0} applications
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Total Loan Value
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">
                    {formatCurrency(stats?.total_loan_value || 0)}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Across {stats?.approved_referrals || 0} approved
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Referral Link Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="lg:col-span-1"
            >
              <Card className="h-full">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <LinkIcon className="w-5 h-5 text-[#00D47E]" />
                    Your Referral Link
                  </CardTitle>
                  <CardDescription>
                    Share this link with customers to track referrals
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-gray-100 rounded-lg p-3 text-sm break-all">
                    {referralLink}
                  </div>
                  
                  <Button 
                    onClick={copyReferralLink}
                    className="w-full bg-black hover:bg-gray-800"
                  >
                    {copied ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-2" />
                        Copy Link
                      </>
                    )}
                  </Button>

                  {qrCodeUrl && (
                    <div className="pt-4 border-t">
                      <p className="text-sm text-gray-500 mb-3 flex items-center gap-2">
                        <QrCode className="w-4 h-4" />
                        QR Code
                      </p>
                      <div className="flex justify-center">
                        <img 
                          src={qrCodeUrl} 
                          alt="Referral QR Code" 
                          className="w-32 h-32 rounded-lg border"
                        />
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full mt-3"
                        onClick={() => window.open(qrCodeUrl, '_blank')}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download QR
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Recent Commissions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="lg:col-span-2"
            >
              <Card className="h-full">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <DollarSign className="w-5 h-5 text-[#00D47E]" />
                    Commission History
                  </CardTitle>
                  <CardDescription>
                    Your recent referral commissions
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {commissions.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p className="font-medium">No commissions yet</p>
                      <p className="text-sm mt-1">Share your referral link to start earning</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {commissions.slice(0, 10).map((commission) => (
                        <div 
                          key={commission.commission_id}
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <p className="font-medium text-black">
                                {commission.lender_name}
                              </p>
                              <Badge className={statusColors[commission.status]}>
                                {commission.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-500 mt-1">
                              Loan: {formatCurrency(commission.loan_amount)} • {formatDate(commission.created_at)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-lg text-[#00D47E]">
                              {formatCurrency(commission.commission_amount)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {commission.commission_rate}% rate
                            </p>
                          </div>
                        </div>
                      ))}
                      
                      {commissions.length > 10 && (
                        <Button variant="ghost" className="w-full mt-4">
                          View All ({commissions.length} total)
                          <ArrowUpRight className="w-4 h-4 ml-2" />
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DealerDashboard;
